import { ExpandMore, Info, ArrowDropDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  styled,
  Typography,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { WsColors, WsIconText, WsTypoColors } from 'wisestep-base';

import { operatorOptions } from '../../../features/leads/helper/leadHelper';

interface WsAccordionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  disabled?: boolean;
  infoText?: string;
  startIcon?: React.ReactElement | null;
  titleColor?: WsTypoColors;
  showOperatorDropdown?: boolean;
  handleOperatorSelect?: (option: { label: string; value: string }) => void;
  defaultOperatorValue?: string;
}

interface ColorProps {
  titleColor?: string;
}

const FilterTitle = styled(Typography)(({ titleColor }: ColorProps) => ({
  fontsize: '14px',
  fontWeight: 'bold',
  color: titleColor ?? WsColors.GenericRichBlack,
}));
const WsAccordion = ({
  title,
  children,
  defaultExpanded = false,
  disabled = false,
  infoText = '',
  startIcon,
  titleColor,
  showOperatorDropdown = false,
  handleOperatorSelect,
  defaultOperatorValue,
}: WsAccordionProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOperator, setSelectedOperator] = useState<{ label: string; value: string } | null>(
    null
  );

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleOperatorSelectOption = (option: { label: string; value: string }) => {
    setSelectedOperator(option);
    if (handleOperatorSelect) handleOperatorSelect(option);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (defaultOperatorValue) {
      const matched = operatorOptions.find((op) => op.value === defaultOperatorValue);
      if (matched) {
        setSelectedOperator(matched);
      }
    } else {
      setSelectedOperator(null);
    }
  }, [defaultOperatorValue]);

  return (
    <Accordion disableGutters={true} defaultExpanded={defaultExpanded} disabled={disabled}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" gap={2} alignItems="center">
          {startIcon && startIcon}
          <FilterTitle color={titleColor}>{title}</FilterTitle>
          {disabled && <WsIconText icon={<Info />} text={infoText} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails aria-disabled>
        {showOperatorDropdown && (
          <>
            <Button
              variant="text"
              onClick={handleDropdownClick}
              endIcon={<ArrowDropDown />}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                fontWeight: 'bold',
                mb: 1,
              }}
            >
              {selectedOperator?.label || 'Select Operator'}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              {operatorOptions.map((option) => (
                <MenuItem key={option.value} onClick={() => handleOperatorSelectOption(option)}>
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default WsAccordion;
